import { Routes, Route, Navigate, useLocation } from "react-router";
import {
  useEffect,
  useState,
  Component,
  lazy,
  Suspense,
  type ReactNode,
} from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { hasUsableAuthToken, useAuthStore } from "@/store/auth.store";
import { isSystemOwner } from "@/lib/authz";

const DashboardPage = lazy(() =>
  import("@/pages/DashboardPage").then((module) => ({
    default: module.DashboardPage,
  })),
);
const TapeChartPage = lazy(() =>
  import("@/pages/TapeChartPage").then((module) => ({
    default: module.TapeChartPage,
  })),
);
const ReservationsPage = lazy(() =>
  import("@/pages/ReservationsPage").then((module) => ({
    default: module.ReservationsPage,
  })),
);
const GuestsPage = lazy(() =>
  import("@/pages/GuestsPage").then((module) => ({
    default: module.GuestsPage,
  })),
);
const HousekeepingPage = lazy(() =>
  import("@/pages/HousekeepingPage").then((module) => ({
    default: module.HousekeepingPage,
  })),
);
const AdminHousekeepingTemplatesPage = lazy(
  () => import("@/pages/AdminHousekeepingTemplatesPage"),
);
const FinancePage = lazy(() =>
  import("@/pages/FinancePage").then((module) => ({
    default: module.FinancePage,
  })),
);
const AsaasAccountPage = lazy(() =>
  import("@/pages/FinancePage").then((module) => ({
    default: module.AsaasAccountPage,
  })),
);
const ReceivablesAnticipationPage = lazy(() =>
  import("@/pages/FinancePage").then((module) => ({
    default: module.ReceivablesAnticipationPage,
  })),
);
const FinancialMovementsPage = lazy(() =>
  import("@/pages/FinancePage").then((module) => ({
    default: module.FinancialMovementsPage,
  })),
);
const SettingsPage = lazy(() =>
  import("@/pages/SettingsPage").then((module) => ({
    default: module.SettingsPage,
  })),
);
const NightAuditPage = lazy(() =>
  import("@/pages/NightAuditPage").then((module) => ({
    default: module.NightAuditPage,
  })),
);
const ChannelManagerPage = lazy(() =>
  import("@/pages/ChannelManagerPage").then((module) => ({
    default: module.ChannelManagerPage,
  })),
);
const LoginPage = lazy(() =>
  import("@/pages/LoginPage").then((module) => ({ default: module.LoginPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("@/pages/ForgotPasswordPage").then((module) => ({
    default: module.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import("@/pages/ResetPasswordPage").then((module) => ({
    default: module.ResetPasswordPage,
  })),
);
const PublicCheckinPage = lazy(() =>
  import("@/pages/PublicCheckinPage").then((module) => ({
    default: module.PublicCheckinPage,
  })),
);
const PublicGuestPortalPage = lazy(() =>
  import("@/pages/PublicGuestPortalPage").then((module) => ({
    default: module.PublicGuestPortalPage,
  })),
);
const PublicBookingEnginePage = lazy(() =>
  import("@/pages/PublicBookingEnginePage").then((module) => ({
    default: module.PublicBookingEnginePage,
  })),
);
const PricingPage = lazy(() =>
  import("@/pages/PricingPage").then((module) => ({
    default: module.PricingPage,
  })),
);
const BillingBlockedPage = lazy(() =>
  import("@/pages/BillingBlockedPage").then((module) => ({
    default: module.BillingBlockedPage,
  })),
);
const PublicDemoPage = lazy(() => import("@/pages/PublicDemoPage").then((module) => ({ default: module.PublicDemoPage })));
const TeamPage = lazy(() =>
  import("@/pages/TeamPage").then((module) => ({ default: module.TeamPage })),
);
const WalletPage = lazy(() =>
  import("@/pages/WalletPage").then((module) => ({
    default: module.WalletPage,
  })),
);
const RoutinesPage = lazy(() =>
  import("@/pages/RoutinesPage").then((module) => ({
    default: module.RoutinesPage,
  })),
);
const AnomaliesPage = lazy(() =>
  import("@/pages/AnomaliesPage").then((module) => ({
    default: module.AnomaliesPage,
  })),
);
const MaintenancePage = lazy(() =>
  import("@/pages/MaintenancePage").then((module) => ({
    default: module.MaintenancePage,
  })),
);
const ReleasesPage = lazy(() =>
  import("@/pages/ReleasesPage").then((module) => ({
    default: module.ReleasesPage,
  })),
);
const AdminQueuePage = lazy(() => import("@/pages/AdminQueuePage"));
const PermissionMatrixPage = lazy(() => import("@/pages/PermissionMatrixPage"));
const InventoryPage = lazy(() => import("@/pages/InventoryPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const StaffMessagesPage = lazy(() => import("@/pages/StaffMessagesPage"));
const AdminTenantsPage = lazy(() => import("@/pages/AdminTenantsPage"));
const AdminPlatformSettingsPage = lazy(
  () => import("@/pages/AdminPlatformSettingsPage"),
);
const AdminFiscalPage = lazy(() => import("@/pages/AdminFiscalPage"));
const AdminWhatsAppPage = lazy(() => import("@/pages/AdminWhatsAppPage"));
const AdminWhatsAppMessagesPage = lazy(() => import("@/pages/AdminWhatsAppMessagesPage"));
const AdminSessionsPage = lazy(() => import("@/pages/AdminSessionsPage"));
const AdminAuditLogsPage = lazy(() => import("@/pages/AdminAuditLogsPage"));
const AdminOnboardingBoardPage = lazy(
  () => import("@/pages/AdminOnboardingBoardPage"),
);
const AdminGuestPortalsPage = lazy(
  () => import("@/pages/AdminGuestPortalsPage"),
);
const AllmightLayout = lazy(() =>
  import("@/components/layout/AllmightLayout").then((module) => ({
    default: module.AllmightLayout,
  })),
);
const GuestPortalsPage = lazy(() =>
  import("@/pages/AdminGuestPortalsPage").then((module) => ({
    default: module.GuestPortalsPage,
  })),
);
const BillingHistoryPage = lazy(() =>
  import("@/pages/BillingHistoryPage").then((module) => ({
    default: module.BillingHistoryPage,
  })),
);
const SetupWizardPage = lazy(() =>
  import("@/pages/SetupWizardPage").then((module) => ({
    default: module.SetupWizardPage,
  })),
);
const NotificationsPage = lazy(() =>
  import("@/pages/NotificationsPage").then((module) => ({
    default: module.NotificationsPage,
  })),
);
const ApiKeysPage = lazy(() => import("@/pages/ApiKeysPage"));
const YieldRulesPage = lazy(() =>
  import("@/pages/YieldRulesPage").then((module) => ({
    default: module.YieldRulesPage,
  })),
);
const ForcePasswordChangePage = lazy(() =>
  import("@/pages/auth/ForcePasswordChangePage").then((module) => ({
    default: module.ForcePasswordChangePage,
  })),
);

// ─── Error Boundary ──────────────────────────────────────────────────────────
class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center gap-4 p-8 text-center">
          <p className="text-lg font-semibold text-destructive">
            Algo deu errado.
          </p>
          <p className="text-sm text-muted-foreground max-w-md">
            Recarregue a página. Se o problema continuar, acione o suporte com o
            horário do erro.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
          >
            Recarregar página
          </button>
          <button
            onClick={() => {
              document.cookie = 'auth_token=; Max-Age=0; path=/';
              document.cookie = 'original_auth_token=; Max-Age=0; path=/';
              document.cookie = 'tenant_id=; Max-Age=0; path=/';
              document.cookie = 'property_id=; Max-Age=0; path=/';
              window.localStorage.removeItem('auth-storage');
              window.location.assign('/entrar');
            }}
            className="px-4 py-2 rounded-xl border border-border text-sm font-medium hover:bg-muted"
          >
            Encerrar sessão corrompida e entrar novamente
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * PrivateRoute — aguarda a hidratação do Zustand persist antes de decidir
 * se redireciona para /entrar. Sem isso, no primeiro render o store está
 * com isAuthenticated=false (estado inicial) e redireciona incorretamente,
 * causando tela preta ao fazer login.
 */
/** Redireciona usuários já autenticados para fora das rotas públicas, como /entrar. */
function PageLoadingFallback() {
  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
      <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  );
}

function PublicRoute({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [hydrated, setHydrated] = useState(() =>
    useAuthStore.persist.hasHydrated(),
  );
  const hasToken = hydrated ? hasUsableAuthToken() : false;

  useEffect(() => {
    if (!hydrated) {
      const unsub = useAuthStore.persist.onFinishHydration(() =>
        setHydrated(true),
      );
      if (useAuthStore.persist.hasHydrated()) setHydrated(true);
      return unsub;
    }
  }, [hydrated]);

  useEffect(() => {
    if (hydrated && isAuthenticated && !hasToken) {
      logout();
    }
  }, [hasToken, hydrated, isAuthenticated, logout]);

  if (!hydrated) return null;
  if (isAuthenticated && hasToken) {
    return (
      <Navigate
        to={user?.must_change_password ? "/alterar-senha" : isSystemOwner(user) ? "/administracao/telemetria" : "/visao-geral"}
        replace
      />
    );
  }

  return <>{children}</>;
}

function PrivateRoute({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const location = useLocation();
  const [hydrated, setHydrated] = useState(() =>
    useAuthStore.persist.hasHydrated(),
  );
  const hasToken = hydrated ? hasUsableAuthToken() : false;

  useEffect(() => {
    // Se ainda não hidratou, esperar o evento de finalização
    if (!hydrated) {
      const unsub = useAuthStore.persist.onFinishHydration(() => {
        setHydrated(true);
      });
      // Confere novamente, pois a hidratação pode ter ocorrido entre o estado e o efeito
      if (useAuthStore.persist.hasHydrated()) {
        setHydrated(true);
      }
      return unsub;
    }
  }, [hydrated]);

  useEffect(() => {
    if (hydrated && isAuthenticated && !hasToken) {
      logout();
    }
  }, [hasToken, hydrated, isAuthenticated, logout]);

  if (!hydrated) {
    // Aguardando localStorage — mostrar spinner mínimo
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || !hasToken) {
    return <Navigate to="/entrar" />;
  }

  if (user?.must_change_password && location.pathname !== "/alterar-senha") {
    return <Navigate to="/alterar-senha" replace />;
  }

  return <>{children}</>;
}

function SystemOwnerRoute({ children }: { children: ReactNode }) {
  const user = useAuthStore((state) => state.user);

  return isSystemOwner(user) ? (
    <>{children}</>
  ) : (
    <Navigate to="/visao-geral" replace />
  );
}

function App() {
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (
        event.key === "auth-storage" &&
        document.visibilityState === "visible"
      ) {
        window.location.reload();
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          {/* Rotas públicas */}
          <Route
            path="/entrar"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/esqueci-minha-senha"
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            }
          />
          <Route
            path="/redefinir-senha"
            element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            }
          />
          <Route path="/pre-cadastro/:token" element={<PublicCheckinPage />} />
          <Route
            path="/hospedagem/:token"
            element={<PublicGuestPortalPage />}
          />
          <Route path="/reservar" element={<PublicBookingEnginePage />} />
          <Route
            path="/reservar/:tenantSlug"
            element={<PublicBookingEnginePage />}
          />
          <Route path="/planos" element={<PricingPage />} />
          <Route path="/demo/visao-geral" element={<PublicDemoPage />} />
          <Route
            path="/assinatura/bloqueada"
            element={<BillingBlockedPage />}
          />
          <Route
            path="/alterar-senha"
            element={
              <PrivateRoute>
                <ForcePasswordChangePage />
              </PrivateRoute>
            }
          />

          {/* Rotas protegidas com layout principal */}
          <Route
            element={
              <PrivateRoute>
                <MainLayout />
              </PrivateRoute>
            }
          >
            <Route path="/" element={<Navigate to="/visao-geral" replace />} />
            <Route path="/visao-geral" element={<DashboardPage />} />
            <Route path="/ocupacao" element={<TapeChartPage />} />
            <Route path="/reservas" element={<ReservationsPage />} />
            <Route path="/hospedes" element={<GuestsPage />} />
            <Route
              path="/portais-dos-hospedes"
              element={<GuestPortalsPage />}
            />
            <Route path="/governanca" element={<HousekeepingPage />} />
            <Route path="/financeiro" element={<FinancePage />} />
            <Route
              path="/financeiro/conta-asaas"
              element={<AsaasAccountPage />}
            />
            <Route
              path="/financeiro/movimentacoes"
              element={<FinancialMovementsPage />}
            />
            <Route
              path="/financeiro/antecipacoes"
              element={<ReceivablesAnticipationPage />}
            />
            <Route path="/auditoria-noturna" element={<NightAuditPage />} />
            <Route
              path="/gerenciador-de-canais"
              element={<ChannelManagerPage />}
            />
            <Route path="/tarifas" element={<YieldRulesPage />} />
            <Route path="/configuracoes" element={<SettingsPage />} />
            <Route path="/configuracoes/equipe" element={<TeamPage />} />
            <Route path="/configuracoes/carteira" element={<WalletPage />} />
            <Route path="/rotinas" element={<RoutinesPage />} />
            <Route path="/alertas-operacionais" element={<AnomaliesPage />} />
            <Route path="/manutencoes" element={<MaintenancePage />} />
            <Route path="/versoes" element={<ReleasesPage />} />
            <Route path="/estoque-e-consumo" element={<InventoryPage />} />
            <Route path="/relatorios" element={<ReportsPage />} />
            <Route path="/mensagens" element={<StaffMessagesPage />} />
            <Route
              path="/financeiro/historico-de-cobrancas"
              element={<BillingHistoryPage />}
            />
            <Route path="/configuracao-inicial" element={<SetupWizardPage />} />
            <Route path="/notificacoes" element={<NotificationsPage />} />
            <Route
              path="/configuracoes/chaves-de-api"
              element={<ApiKeysPage />}
            />
          </Route>

          <Route
            element={
              <PrivateRoute>
                <SystemOwnerRoute>
                  <AllmightLayout />
                </SystemOwnerRoute>
              </PrivateRoute>
            }
          >
            <Route path="/administracao/configuracoes-da-plataforma" element={<AdminPlatformSettingsPage />} />
            <Route path="/administracao/telemetria" element={<AdminPlatformSettingsPage />} />
            <Route path="/administracao/propriedades" element={<AdminTenantsPage />} />
            <Route path="/administracao/implantacoes" element={<AdminOnboardingBoardPage />} />
            <Route path="/administracao/portais-dos-hospedes" element={<AdminGuestPortalsPage />} />
            <Route path="/administracao/notas-fiscais" element={<AdminFiscalPage />} />
            <Route path="/administracao/whatsapp" element={<AdminWhatsAppPage />} />
            <Route path="/administracao/whatsapp/mensagens" element={<AdminWhatsAppMessagesPage />} />
            <Route path="/administracao/sessoes" element={<AdminSessionsPage />} />
            <Route path="/administracao/auditoria" element={<AdminAuditLogsPage />} />
            <Route path="/administracao/filas" element={<AdminQueuePage />} />
            <Route path="/administracao/permissoes" element={<PermissionMatrixPage />} />
            <Route path="/administracao/modelos-de-governanca" element={<AdminHousekeepingTemplatesPage />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/visao-geral" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
